import { mapGetters } from 'vuex';
import { normalizeErrorMessage } from '@/utils/error-message';
import mitraCourseListFilter from '@/mixins/mitraCourseListFilter';
import {
  APPROVAL_STEP_STATUS_OPTIONS,
  COURSE_TYPE_OPTIONS,
  TNA_COURSE_STATUS_OPTIONS
} from '@/constants/mitraFilters';

export default {
  name: 'TNAMitra',
  mixins: [mitraCourseListFilter],
  data() {
    return {
      selectedCourse: null,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100],
      filterModalId: 'modal-filter-tna-mitra',
      statusQueryKey: 'tna_status',
      filters: {
        search: '',
        status: null,
        approval_step: null,
        course_type: null
      },
      statusOptions: TNA_COURSE_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_STATUS_OPTIONS,
      courseTypeOptions: COURSE_TYPE_OPTIONS,
      selectedPeriod: null,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      isDownloading: false
    };
  },
  computed: {
    ...mapGetters('tnaMitra', ['courses', 'pagination', 'loading', 'error', 'summary', 'summaryLoading']),
    ...mapGetters('tnaPeriod', { periods: 'periods', periodsLoading: 'loading' }),
    
    // Period dropdown options from store
    periodOptions() {
      if (!this.periods || this.periods.length === 0) return [];
      
      // Only show active periods
      return this.periods
        .map(p => ({
          label: this.formatPeriodLabel(p.start_date, p.end_date),
          value: p.id
        }));
    },
    isLoading() {
      return this.loading;
    },
    filteredCourses() {
      let filtered = this.courses || [];
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase();
        filtered = filtered.filter(course => 
          course.title.toLowerCase().includes(searchLower) ||
          course.description?.toLowerCase().includes(searchLower) ||
          course.provider_name?.toLowerCase().includes(searchLower)
        );
      }
      if (this.filters.status) {
        filtered = filtered.filter(course => 
          course.tna_status === this.filters.status
        );
      }
      return filtered;
    },
    paginatedCourses() {
      return this.courses || [];
    },
    totalItems() {
      return this.pagination?.total || 0;
    },
    totalPages() {
      return this.pagination?.totalPage || 0;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        // Show all pages if total is 7 or less
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        // Show pages around current page
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        // Always show last page
        pages.push(total);
      }
      
      return pages;
    }
  },
  watch: {
    'filters.search'() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    currentPage() {
      this.fetchCourses();
    },
    selectedPeriod(newPeriod) {
      if (newPeriod) {
        this.currentPage = 1;
        this.fetchSummary();
        this.fetchCourses();
      }
    },
  },
  methods: {
    async fetchCourses() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
        };
        if (this.selectedPeriod) {
          params.tna_period_id = this.selectedPeriod;
        }
        if (this.filters.search) {
          params.search = this.filters.search;
        }
        this.applyCourseListFilterParams(params);
        await this.$store.dispatch('tnaMitra/getCourses', params);
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.$swal({
          title: 'Gagal Memuat Data',
          text: normalizeErrorMessage(error, 'Data pelatihan belum berhasil dimuat. Silakan coba lagi.'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    viewCourseDetail(course) {
      console.log('📖 View course detail:', course.title);
      this.selectedCourse = course;
      this.$nextTick(() => {
        this.$bvModal.show('modal-tna-mitra-detail');
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    clearFilters() {
      this.filters = {
        ...this.filters,
        search: '',
        status: null,
        approval_step: null,
        course_type: null
      };
      this.currentPage = 1;
    },
    async fetchSummary() {
      if (!this.selectedPeriod) return;
      
      try {
        await this.$store.dispatch('tnaMitra/getSummary', {
          tnaPeriodId: this.selectedPeriod,
          params: {}
        });
      } catch (error) {
        console.error('Error fetching summary:', error);
        // Summary error is handled silently, default values will be shown
      }
    },
    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return '';
      
      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },
    getDefaultPeriodId() {
      if (!Array.isArray(this.periods) || this.periods.length === 0) {
        return null;
      }

      const openPeriod = this.periods.find((period) => {
        return period.vstatus.toLowerCase() == 'open';
      });

      return openPeriod?.id || null;
    },
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text'; // Certification
      } else if (type.includes('semi')) {
        return 'fe fe-shield'; // Semi Certification
      } else if (type.includes('training')) {
        return 'fe fe-user'; // Training
      }
      
      return 'fe fe-award'; // Default icon
    },
    getStatusBadgeClass(status) {
      const classes = {
        'SCHEDULE': 'badge bg-success',
        'NO SCHEDULE': 'badge bg-warning',
        'COMPLETED': 'badge bg-info',
        'CANCELLED': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },
    async downloadTemplate() {
      if (this.isDownloading) return;
      
      this.isDownloading = true;
      try {
        const response = await this.$store.dispatch('tnaMitra/downloadBulkTemplate', {});
        
        // Create blob and download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'TNA_Partner_Template.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$swal({
          title: 'Success',
          text: 'Template berhasil didownload.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal({
          title: 'Gagal Download Template',
          text: 'Template belum berhasil didownload. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isDownloading = false;
      }
    },
    showBulkUploadModal() {
      this.$bvModal.show('modal-bulk-upload-tna');
    },
    handleUploadSuccess(result) {
      console.log('✅ Upload success:', result);
      // Refresh courses after successful upload
      this.fetchCourses();
      this.fetchSummary();
    }
  },
  async mounted() {
    console.log('✅ TNA Mitra page mounted');
    
    // Fetch TNA periods first
    try {
      await this.$store.dispatch('tnaPeriod/getTNAPeriods');
      
      // Set current year period as default (fallback to first option)
      const defaultPeriodId = this.getDefaultPeriodId();
      if (defaultPeriodId) {
        this.selectedPeriod = defaultPeriodId;
      } else {
        this.fetchCourses();
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
      this.fetchCourses();
    }
  }
};
